import { forwardRef } from 'react';
import { Disableable } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { ensureString } from '../utils';
import { ChipContainer, ChipIconButton, ChipText } from './style';

export type ChipValue = string | number | boolean;
export interface ChipProps extends Disableable {
    children: React.ReactNode;
    value?: ChipValue;
    onRemove?: (event: React.SyntheticEvent, value: ChipValue) => void;
    error?: boolean;
    cancelEvent?: boolean;
    showRemoveButton?: boolean;
    readOnly?: boolean;
}

export const Chip = forwardRef<HTMLDivElement, ChipProps>(
    (
        {
            children,
            value,
            onRemove,
            disabled = false,
            error = false,
            cancelEvent = true,
            showRemoveButton = true,
            readOnly = false,
            ...other
        },
        ref
    ) => {
        const { t } = useLocalization();

        const handleRemoveButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
            if (cancelEvent) {
                event.stopPropagation();
            }
            onRemove?.(event, value ?? '');
        };

        const chipContentString = ensureString({ value: children }) || '';
        const removeTextString = ensureString({ value: t(OptimusTextKey.Remove) }) || '';

        return (
            <ChipContainer
                ref={ref}
                disabled={disabled}
                error={error}
                showRemoveButton={showRemoveButton}
                readOnly={readOnly}
                {...other}
            >
                <ChipText data-test-chip-text disabled={disabled} error={error} readOnly={readOnly}>
                    {children}
                </ChipText>
                {showRemoveButton && !readOnly ? (
                    <ChipIconButton
                        icon={IconName.CrossClose}
                        onClick={handleRemoveButtonClick}
                        size="small"
                        data-test-button-remove
                        disabled={disabled}
                        error={error}
                        aria-label={
                            chipContentString ? `${removeTextString} ${chipContentString}`.trim() : removeTextString
                        }
                    />
                ) : null}
            </ChipContainer>
        );
    }
);
